"use client";

import { formatNumberWithCommas } from "@alba-cars/common-modules";
import clsx from "clsx";
import debounce from "lodash.debounce";
import { useEffect, useMemo, useState } from "react";

interface RangeInputProps {
  label?: string;
  onFromChange?: (value: string) => void;
  onToChange?: (value: string) => void;
  onFromClear?: () => void;
  onToClear?: () => void;
  onReset?: () => void;
  className?: string;
  fromValue?: string | null;
  toValue?: string | null;
  skipFormatting?: boolean;
}

export default function RangeInput({
  label,
  onFromChange,
  onToChange,
  onFromClear,
  onToClear,
  onReset,
  className,
  fromValue,
  toValue,
  skipFormatting = false,
}: RangeInputProps) {
  const [fromValueState, setFromValueState] = useState(fromValue ?? "");
  const [toValueState, setToValueState] = useState(toValue ?? "");

  useEffect(() => {
    setFromValueState(fromValue ? (!skipFormatting ? formatNumberWithCommas(fromValue.toString()) : fromValue) : "");
    setToValueState(toValue ? (!skipFormatting ? formatNumberWithCommas(toValue.toString()) : toValue) : "");
  }, [fromValue, skipFormatting, toValue]);

  const debouncedHandlers = useMemo(
    () => ({
      fromChange: debounce((value: string) => {
        onFromChange?.(value);
      }, 400),
      toChange: debounce((value: string) => {
        onToChange?.(value);
      }, 400),
    }),
    [onFromChange, onToChange],
  );

  const formatInput = (value: string): string => {
    const numbersOnly = value.replace(/[^\d]/g, "");
    if (!numbersOnly) return "";
    return formatNumberWithCommas(numbersOnly);
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatInput(e.target.value);
    setFromValueState(formattedValue);
    debouncedHandlers.fromChange(formattedValue);
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatInput(e.target.value);
    setToValueState(formattedValue);
    debouncedHandlers.toChange(formattedValue);
  };

  const handleReset = () => {
    setFromValueState("");
    setToValueState("");
    onFromChange?.("");
    onToChange?.("");
    onReset?.();
  };

  // Cleanup debounced functions
  useEffect(() => {
    return () => {
      debouncedHandlers.fromChange.cancel();
      debouncedHandlers.toChange.cancel();
    };
  }, [debouncedHandlers]);

  return (
    <div className={clsx("w-full", className)}>
      <div className="mb-2 flex items-center justify-between">
        <span className="inter text-sm font-semibold text-[#000000]">{label}</span>
        <button
          onClick={handleReset}
          className="inter text-sm text-secondary-foreground transition-colors hover:cursor-pointer hover:text-primary"
        >
          Reset
        </button>
      </div>
      <div className="relative flex rounded-2xl border border-[#EEF1F6] bg-secondary-light p-5 transition-all duration-300 hover:border-grey-border">
        <input
          type="text"
          placeholder="From"
          value={fromValueState}
          onChange={handleFromChange}
          className="inter placeholder:inter h-full w-full bg-transparent placeholder:text-base placeholder:text-secondary-foreground focus:outline-none"
        />
        <div className="absolute left-1/2 top-1/2 h-[100%] w-[2px] -translate-x-1/2 -translate-y-1/2 bg-[#EEF1F6]"></div>
        <input
          type="text"
          placeholder="To"
          value={toValueState}
          onChange={handleToChange}
          className="inter placeholder:inter ms-10 h-full w-full bg-transparent placeholder:text-base placeholder:text-secondary-foreground focus:outline-none"
        />
      </div>
    </div>
  );
}
